// react
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Skeleton } from '@/components/atoms/Skeleton/Skeleton';

// store
import { Article } from '@karehero/models';

// utils
import { buildImageUrl } from '@/utils/sanity';

/**
 * ArticleFeatured is a container for featured article content.
 */
export const ArticleFeatured = ({
  article,
  onClick,
  onBookmark,
  isBookmarked,
  isLoading,
}: ArticleFeaturedProps) => {
  // memos
  const headerImageUrl = useMemo(
    () => buildImageUrl(article?.headerImageUrl, 800),
    [article],
  );

  return (
    <LoadingTransition
      isLoading={isLoading}
      loading={
        <ContentItem>
          <Skeleton shape='header' isSquare />
        </ContentItem>
      }
      content={
        <ContentItem onClick={() => article && onClick?.(article)}>
          <ImageWrapper>
            <img
              src={headerImageUrl}
              alt='header'
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src =
                  'https://cdn.sanity.io/images/x7r1t5o8/production/e8579e2a4c3766cf0cf99a24a9d0679d2d2bbeb4-5322x2365.jpg?h=800';
              }}
            />
          </ImageWrapper>
          <FadeBottom />
          <ContentBody>
            <ContentBodyHeader>
              <ContentTags>
                {article?.tags?.map((tag, i) => (
                  <React.Fragment key={`${article.title}-${tag.label}`}>
                    <ContentTag>{tag.label}</ContentTag>
                    {article?.tags && i !== article?.tags?.length - 1 && (
                      <div>-</div>
                    )}
                  </React.Fragment>
                ))}
              </ContentTags>
              <BookmarkWrapper
                $isBookmarked={isBookmarked}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  article && onBookmark?.(article);
                }}
              >
                <Icon icon='bookmark' type={isBookmarked ? 'fas' : 'fal'} />
              </BookmarkWrapper>
            </ContentBodyHeader>
            <ContentTitle>{article?.title}</ContentTitle>
            <ContentDescription>{article?.description}</ContentDescription>
          </ContentBody>
        </ContentItem>
      }
    />
  );
};

const ContentItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  width: 100%;
  height: 300px;
  max-height: 300px;
  box-sizing: border-box;
  background: ${(props) => props.theme.color.neutral[10]};
  box-shadow: ${(props) => props.theme.elevation[1]};
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  border: 1px solid ${(props) => props.theme.color.neutral[40]};
  text-decoration: none;

  transition-duration: 0.2s;
  &:hover {
    transform: scale(1.02);
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;
  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    user-select: none;
    user-drag: none;
    -webkit-user-drag: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
`;

const FadeBottom = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
`;

const ContentBody = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: ${(props) => props.theme.spacing[32]};
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[4]};
`;

const ContentBodyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const ContentTags = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
  font: ${(props) => props.theme.typography.body.xs.regular};
  color: ${(props) => props.theme.color.neutral[30]};
  overflow: hidden;
`;

const ContentTag = styled.div`
  font: ${(props) => props.theme.typography.body.xs.regular};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContentTitle = styled.div`
  font: ${(props) => props.theme.typography.body.xl.semibold};
  color: ${(props) => props.theme.color.neutral[10]};
`;

const ContentDescription = styled.div`
  display: -webkit-box;
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[10]};
`;

interface BookmarkWrapperProps {
  $isBookmarked?: boolean;
}
const BookmarkWrapper = styled.div<BookmarkWrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 99999px;
  color: ${(props) => props.theme.color.neutral[10]};

  ${(props) =>
    props.$isBookmarked &&
    css`
      color: ${props.theme.color.primary[80]};
    `}

  transition: background 0.2s;
  &:hover {
    background: ${(props) => props.theme.color.neutral[70]};
  }
`;

interface ArticleFeaturedProps {
  article?: Partial<Article>;
  onClick?: (article: Partial<Article>) => void;
  onBookmark?: (article: Partial<Article>) => void;
  isBookmarked?: boolean;
  isLoading?: boolean;
}

ArticleFeatured.displayName = 'ArticleFeatured';

export default ArticleFeatured;
